/**
 * A row of quiet horizontal tabs on a rule, heading the list it switches,
 * with an optional trailing link on the same rule. Spec beside it in
 * tab-bar.md.
 *
 * @module components/ui/tab-bar
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { tabStripEdges, type TabStripEdges } from "./tabStripScroll";
import { Tabs, TabsList, TabsTab } from "./tabs";

/** Which ends of a narrow bar hide tabs, so those ends fade instead of cutting a label. */
function useOverflowEdges() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState<TabStripEdges>({ start: false, end: false });
  useEffect(() => {
    const list = ref.current;
    if (list === null) return;
    const measure = () => {
      const next = tabStripEdges(list);
      setEdges((previous) => (previous.start === next.start && previous.end === next.end ? previous : next));
    };
    measure();
    const resize = new ResizeObserver(measure);
    resize.observe(list);
    list.addEventListener("scroll", measure, { passive: true });
    return () => {
      resize.disconnect();
      list.removeEventListener("scroll", measure);
    };
  }, []);
  return { ref, edges };
}

export interface TabBarItem<Id extends string> {
  readonly id: Id;
  readonly label: string;
  /** Shown after the label when above zero; `label` is what a screen reader hears for it ("1 failing"). */
  readonly count?: { readonly value: number; readonly label: string };
}

export function TabBar<Id extends string>({
  items,
  value,
  onValueChange,
  trailing,
  children,
  className,
}: {
  readonly items: ReadonlyArray<TabBarItem<Id>>;
  readonly value: Id;
  readonly onValueChange: (id: Id) => void;
  /** A link on the bar's trailing end, never a tab (e.g. "View all"). */
  readonly trailing?: ReactNode;
  /** The panels (`TabsPanel`), under the bar. */
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const overflow = useOverflowEdges();
  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        const item = items.find((candidate) => candidate.id === next);
        if (item) onValueChange(item.id);
      }}
      className={className}
    >
      {/* Wider than the column by each tab's padding, so the first label and the rule start on the column's edge. */}
      <div
        data-slot="tab-bar"
        className="relative -mx-2 flex shrink-0 items-center before:absolute before:inset-x-2 before:bottom-0 before:h-px before:bg-border"
      >
        <TabsList
          ref={overflow.ref}
          variant="quiet"
          activateOnFocus
          className={cn(
            "flex-1 [--fade-size:32px]",
            overflow.edges.start && "mask-l-from-[calc(100%-var(--fade-size))]",
            overflow.edges.end && "mask-r-from-[calc(100%-var(--fade-size))]",
          )}
        >
          {items.map((item) => {
            const count = item.count !== undefined && item.count.value > 0 ? item.count : null;
            return (
              <TabsTab
                key={item.id}
                value={item.id}
                variant="quiet"
                data-slot="tab-bar-tab"
                aria-label={count ? `${item.label}, ${count.label}` : undefined}
              >
                {item.label}
                {count ? <span className="text-xs tabular-nums text-muted-foreground">{count.value}</span> : null}
              </TabsTab>
            );
          })}
        </TabsList>
        {trailing}
      </div>
      {children}
    </Tabs>
  );
}
